import React, { useState } from 'react';
import styled from 'styled-components';
import * as Yup from 'yup';
import { Auth } from 'utils/ExportCommon';
import { media } from 'styles/Mixins';
import { font } from 'styles/Constants';
import { Formik } from 'formik';
import { AdvisorPrimaryButton } from 'styles/Buttons';
import MobilePhone from 'components/shared/PhoneCodeComponent';
import Spinner from 'components/shared/Spinner';
import SuccessFinalProcess from 'modules/agencies/shared/SuccessFinalProcessComponent';
import { CloseButton } from 'styles/Modals';
import {
  ContainerView,
  StyledLinkButton,
  StyledValidatedInput,
  StyledErrorMessage,
  StyledForm
} from 'modules/agencies/shared/style/authentication/FormStyle';
import NotifyConfirmation from 'actions/NotifyConfirmationActions';
import swal from '@sweetalert/with-react';
import { StyledScreenOverlay, ModalCard, ModalBody } from 'styles/Modals';

type PropsType = {
  open: boolean,
  phoneValue?: string,
  phoneCountryCode?: string,
  userId?: string,
  onClose?: () => void,
  updateData: (code: any, mobilePhone: string) => void
};

const TextModal = styled.div`
  margin-top: 21px;
  text-align: center;
  font-family: ${font.openSans};
  font-size: 15px;
  line-height: 19px;
  text-align: left;
  color: #707070;
  ${media.xs`
	  width: 305px;
	  margin-left: 5px;
  `}
`;

const SubmitButton = styled(AdvisorPrimaryButton)`
  width: 100%;
  margin-bottom: 20px;
`;

const StyledPhoneErrorMessage = styled(StyledErrorMessage)`
  position: static;
  text-align: left;
  top: 0;
`;

const Whatsapp = styled.img.attrs({
  src: Auth.Whatsapp
})`
  float: right;
  position: absolute;
  top: 6px;
  right: 9px;
`;

const Wrapper = styled.div`
  justify-content: center;
  display: flex;
  flex-direction: column;
  margin-top: 32px;
`;
const LinkWrapper = styled.div`
  justify-content: center;
  display: flex;
  flex-direction: column;
  margin-top: 4px;
`;

const StyledCloseButton = styled(CloseButton)`
  color: #748796;
  top: 15px;
  right: 15px;
  width: 15px;
  line-height: 15px;
  font-size: 30px;
  text-align: center;
`;

const ModalContent = styled(ModalBody)`
  padding-top: 14px;
`;

type FormikForm = {
  mobilePhone: string
};

const ChangeMobilePhoneModal = ({
  open,
  phoneCountryCode,
  onclose,
  phoneValue,
  userId,
  updateData
}: PropsType) => {
  const [pickingCountryCode, setPickingCountryCode] = useState(false);
  const [countryCode, setCountryCode] = useState(phoneCountryCode);
  const [loading, setLoading] = useState(false);
  const [successModal, setSuccessModal] = useState(false);

  const closeModal = (value: boolean) => {
    onclose(value);
  };

  const toggleCountryCodeSelect = () =>
    setPickingCountryCode(prevState => !prevState);

  const defaultValues = (): FormikForm => ({
    mobilePhone: phoneValue
  });

  const validationSchema = () => {
    return Yup.object().shape({
      mobilePhone: Yup.string()
        .required('Campo requerido')
        .matches(
          /^[0-9]{10}$/,
          'Ingresa un número de teléfono válido (10 dígitos)'
        )
    });
  };

  const handleCountryCodeChange = ({ value }: any) => {
    setCountryCode(value);
    toggleCountryCodeSelect();
  };

  const submitForm = (values: FormikForm, { resetForm }: any): void => {
    setLoading(true);
    const cleanCountryCode = countryCode.toString().replace('+', '');
    const data = {
      mobile_phone: `+${cleanCountryCode}${values.mobilePhone}`,
      phone_country_code: `+${cleanCountryCode}`
    };

    NotifyConfirmation.updateMobilePhone(userId, data).then(
      () => {
        updateData(countryCode, values.mobilePhone);
        setSuccessModal(true);
        setLoading(false);
      },
      () => {
        onclose(false);
        setSuccessModal(false);
        setLoading(false);
        swal({
          title: '¡Ha ocurrido un error!',
          icon: 'error',
          timer: 5000
        });
      }
    );
  };

  const renderForm = (formik: any) => {
    const { errors, touched } = formik;
    return (
      <StyledForm autoComplete="off">
        <StyledValidatedInput>
          <MobilePhone
            placeHolder="Número de whatsapp"
            handleCountryCodeClick={toggleCountryCodeSelect}
            handleCountryCodePicked={handleCountryCodeChange}
            pickingCountryCode={pickingCountryCode}
            countryCode={+countryCode}
            errors={errors.mobilePhone}
            touched={touched.mobilePhone}
            name="mobilePhone"
          />
          <Whatsapp />
          <StyledPhoneErrorMessage
            name="mobilePhone"
            component="div"
            fontSize="13"
          />
        </StyledValidatedInput>
        <Wrapper>
          <SubmitButton type="submit">
            {loading ? <Spinner color="#fff" /> : 'Guardar cambios'}
          </SubmitButton>
        </Wrapper>
      </StyledForm>
    );
  };

  return (
    <StyledScreenOverlay show={open}>
      <ModalCard>
        <ModalContent>
          {!successModal && (
            <TextModal>
              Este es el número de celular que tenemos registrado
            </TextModal>
          )}
          <ContainerView paddingView={successModal ? 'none' : '20px'}>
            {!successModal && (
              <Formik
                initialValues={defaultValues()}
                validationSchema={validationSchema()}
                onSubmit={submitForm}
                render={renderForm}
              />
            )}
            {successModal && (
              <>
                <StyledCloseButton
                  onClick={() => {
                    setSuccessModal(false);
                    closeModal(false);
                  }}
                >
                  &times;
                </StyledCloseButton>
                <SuccessFinalProcess
                  showButtonFunction={false}
                  showlinkFunction={false}
                  principalTitle="¡Listo!"
                  subTitle="Hemos guardado el nuevo número y enviado el código nuevamente"
                  buttonFunction={() => {}}
                  linkFunction={() => {}}
                  classes={{
                    marginTop: '25px',
                    marginBottom: '21px'
                  }}
                />
              </>
            )}
            <LinkWrapper>
              {!successModal && (
                <StyledLinkButton
                  onClick={() => {
                    closeModal(false);
                    setSuccessModal(false);
                  }}
                >
                  Cancelar
                </StyledLinkButton>
              )}
            </LinkWrapper>
          </ContainerView>
        </ModalContent>
      </ModalCard>
    </StyledScreenOverlay>
  );
};

export default ChangeMobilePhoneModal;
